import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildApiUrl } from '../../../utils/apiBase.js';
import NotificationModal from './NotificationModal.jsx';
import './NotificationModal.css';

/** お知らせ同期のポーリング間隔（5 分）— Firestore 監視の代替 */
const NOTIFICATION_POLL_MS = 5 * 60 * 1000;

const NotificationBell = ({ user, clinic }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [uid, setUid] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const bellRef = useRef(null);
  const syncingRef = useRef(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(buildApiUrl('/api/notifications'), { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUid(data.uid || null);
    } catch {
      // silent
    }
  }, []);

  const syncAndFetch = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    try {
      await fetch(buildApiUrl('/api/notifications/sync'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      await fetchNotifications();
    } catch {
      // silent
    } finally {
      syncingRef.current = false;
    }
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user || !clinic) return;

    syncAndFetch();
    const intervalId = setInterval(syncAndFetch, NOTIFICATION_POLL_MS);

    return () => clearInterval(intervalId);
  }, [user, clinic, syncAndFetch]);

  useEffect(() => {
    if (searchParams.get('open') !== 'notifications') return;
    setShowModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await fetch(buildApiUrl(`/api/notifications/${notificationId}/read`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true, readAt: { seconds: Math.floor(Date.now() / 1000) } } : n)
      );
    } catch {
      // silent
    }
  }, []);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <>
      <button
        ref={bellRef}
        className="hub-notification-bell-btn"
        onClick={() => setShowModal(true)}
        title="お知らせ"
        aria-label={`お知らせ${unreadCount > 0 ? ` (${unreadCount}件の未読)` : ''}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="hub-notification-bell-dot" aria-hidden="true" />
        )}
      </button>

      {showModal && (
        <NotificationModal
          notifications={notifications}
          uid={uid}
          onClose={() => setShowModal(false)}
          onMarkRead={markAsRead}
        />
      )}
    </>
  );
};

export default NotificationBell;
